import React, { useState, useEffect, useMemo, useRef } from "react";

import {
  Paper,
  Box,
  Typography,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Select,
  FormControl,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { BorderTop, Visibility, VisibilityOff } from "@mui/icons-material";
import SplitscreenIcon from "@mui/icons-material/Splitscreen";
import { CircularProgress } from "@mui/material";
import { format, parseISO } from "date-fns";
import PlagiarismIcon from "@mui/icons-material/Plagiarism";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import { WidgetContainer } from "../widgetContainer/widgetContainer";
import { DownloadExcel } from "../../../functions/downloadExcel";
import { useTheme } from "@mui/material";

const GenericTable = ({
  data = [],
  title = "Data Table",
  ignoredFields = [],
  defaultColumns = [], // If empty, all columns will be shown
  columnNameMapping = {},
  maxColumnWidths = {},
  dollarColumns = [],
  showSearch = false,
  headerColor = "#505050",
  showActionColumn = false,
  discription = "",
  customColumns = [],
  searchQuery = null,
  allCurrency = false,
  showTotalRow = false,
  totalColumn = null,
  alternateRowColor = true,
  defaultSort = null,
  maximize = false,
  loading = true,
  allowDownload = true,
  customWidgetActions = [],
  onActionClick = () => {},
}) => {
  const hasDefaultSorted = useRef(false);
  const validData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(validData);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [anchorEl, setAnchorEl] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);

  const [searchColumns, setSearchColumns] = useState([]);
  const [columnFilter, setColumnFilter] = useState("");
  const [lastUpdatedDate, setLastUpdatedDate] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchOperator, setSearchOperator] = useState("contains");
  const [internalSearchQuery, setInternalSearchQuery] = useState(searchQuery);
  const theme = useTheme();

  const styles = {
    actionsTh: {
      padding: "8px",
      textAlign: "center",
      color: "#fff",
      borderBottom: "1px solid #fff",
      backgroundColor: headerColor,
      textTransform: "uppercase",
      fontWeight: "bold",
      fontSize: "14px",
      height: "40px",
      cursor: "pointer",
      maxWidth: "200px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    th: {
      padding: "8px",
      textAlign: "left",
      color: "#fff",
      borderBottom: "1px solid #fff",
      backgroundColor: headerColor,
      textTransform: "uppercase",
      fontWeight: "bold",
      fontSize: "14px",
      height: "40px",
      cursor: "pointer",
      maxWidth: "200px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    td: {
      padding: "8px",
      textAlign: "left",
      color: theme.palette.text.primary,
      // "#eee",
      borderBottom: "1px solid #9E9B9F",
      overflow: "hidden",
      fontSize: "13px",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    actionsTd: {
      padding: "8px",
      textAlign: "center",
      color: "#eee",
      borderBottom: "1px solid #9E9B9F",
      overflow: "hidden",
      fontSize: "13px",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    firstTh: {},
    lastTh: {},
  };

  const getOrdinal = (day) => {
    if (day >= 11 && day <= 13) return `${day}th`; // Special case for 11th, 12th, 13th
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  };

  // Get all column names from validData, ignoring keys in ignoredFields and any numeric keys.
  const allColumns = useMemo(() => {
    const uniqueKeys = new Set();
    validData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (!ignoredFields.includes(key) && isNaN(key)) {
          uniqueKeys.add(key);
        }
      });
    });
    return Array.from(uniqueKeys);
  }, [validData, ignoredFields]);

  const formatLastUpdated = (lastUpdatedDate) => {
    if (!lastUpdatedDate) return "";

    const date = parseISO(lastUpdatedDate);
    const dayWithOrdinal = getOrdinal(parseInt(format(date, "d"), 10)); // Get day + ordinal suffix

    return `${format(date, "h:mm a EEE")} ${dayWithOrdinal} of ${format(date, "MMMM")}`;
  };

  useEffect(() => {
    if (validData.length > 0 && validData[0].timestamp) {
      setLastUpdatedDate(formatLastUpdated(validData[0].timestamp));
    }
  }, [validData]);

  // If defaultColumns is empty, show all columns; otherwise, hide those not in defaultColumns.
  const didSetInitialHidden = useRef(false);

  useEffect(() => {
    if (!didSetInitialHidden.current && allColumns.length > 0) {
      setHiddenColumns((prevHidden) => {
        const newHidden =
          defaultColumns.length === 0 && customColumns.length === 0
            ? []
            : allColumns.filter((key) => !defaultColumns.includes(key));

        didSetInitialHidden.current = true;
        return newHidden;
      });
    }
  }, [defaultColumns, allColumns]);

  const [visibleCount, setVisibleCount] = useState(100);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!tableContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;

      // Near bottom threshold (100px)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        setVisibleCount((prev) => Math.min(prev + 100, filteredData.length));
      }
    };

    const container = tableContainerRef.current;
    if (container) container.addEventListener("scroll", handleScroll);

    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
  }, [filteredData.length]);

  // Compute visible columns dynamically.
  const visibleColumns = useMemo(() => {
    const orderedColumns =
      defaultColumns.length > 0
        ? [...defaultColumns, ...allColumns.filter((col) => !defaultColumns.includes(col))]
        : allColumns;

    return orderedColumns.filter((col) => !hiddenColumns.includes(col));
  }, [allColumns, defaultColumns, hiddenColumns]);

  const applyFilters = () => {
    setIsFiltering(true);
    setTimeout(() => {
      let filtered = validData;

      if (searchTerm || searchQuery) {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const columnsToSearch = searchColumns.length > 0 ? searchColumns : allColumns;

        filtered = filtered.filter((row) => {
          let matchesSearchTerm = true;
          let matchesSearchQuery = true;

          // ✅ Basic text search
          if (searchTerm) {
            matchesSearchTerm = columnsToSearch.some((col) => {
              if (!row[col]) return false;
              const value = row[col].toString().toLowerCase();

              switch (searchOperator) {
                case "contains":
                  return value.includes(lowerCaseSearchTerm);
                case "equals":
                  return value === lowerCaseSearchTerm;
                case "starts_with":
                  return value.startsWith(lowerCaseSearchTerm);
                case "ends_with":
                  return value.endsWith(lowerCaseSearchTerm);
                default:
                  return false;
              }
            });
          }

          // ✅ Structured filter
          if (internalSearchQuery && typeof internalSearchQuery === "object") {
            matchesSearchQuery = Object.entries(internalSearchQuery).every(([field, condition]) => {
              const { operator, value } = condition;
              const fieldValue = row[field];
              if (fieldValue === undefined || fieldValue === null) return false;

              switch (operator) {
                case "=":
                  return fieldValue == value;
                case "!=":
                  return fieldValue != value;
                case "contains":
                  return fieldValue
                    .toString()
                    .toLowerCase()
                    .includes(value.toString().toLowerCase());
                case "starts_with":
                  return fieldValue
                    .toString()
                    .toLowerCase()
                    .startsWith(value.toString().toLowerCase());
                case "ends_with":
                  return fieldValue
                    .toString()
                    .toLowerCase()
                    .endsWith(value.toString().toLowerCase());
                default:
                  return true;
              }
            });
          }

          return matchesSearchTerm && matchesSearchQuery;
        });
      }

      if (sortConfig.key) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = sortConfig.isCustom ? sortConfig.scoreFn?.(a) : a[sortConfig.key];
          const bVal = sortConfig.isCustom ? sortConfig.scoreFn?.(b) : b[sortConfig.key];

          if (aVal == null) return 1;
          if (bVal == null) return -1;

          if (typeof aVal === "string") {
            return sortConfig.direction === "ascending"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }

          return sortConfig.direction === "ascending" ? aVal - bVal : bVal - aVal;
        });
      }

      setFilteredData(filtered);
      setIsFiltering(false);
    }, 50);
  };

  useEffect(() => {
    setInternalSearchQuery(searchQuery); // Sync external query
  }, [searchQuery]);

  useEffect(() => {
    // If we have a default sort and it hasn't been applied yet, skip this effect
    // Let the default sort effect handle the initial filtering/sorting
    if (defaultSort?.key && !hasDefaultSorted.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      applyFilters();
    }, 50); // slight debounce, same as applyFilters' own timeout

    return () => clearTimeout(timeoutId); // prevent loops or stacking
  }, [searchTerm, searchColumns, internalSearchQuery, validData, searchOperator]);

  // ✅ NEW: Now listens to `searchOperator`
  const previousDataRef = useRef(null);
  const previousSortConfigRef = useRef(null);

  useEffect(() => {
    // 🔁 Reset sort if data changes meaningfully
    const dataChanged = JSON.stringify(previousDataRef.current) !== JSON.stringify(validData);

    if (dataChanged) {
      hasDefaultSorted.current = false;
      previousDataRef.current = validData;
    }

    // Apply default sort config when data loads (but don't set filteredData directly)
    // Let applyFilters handle actually filtering/sorting the data
    if (!hasDefaultSorted.current && defaultSort?.key && validData.length > 0 && !loading) {
      setSortConfig({
        key: defaultSort.key,
        direction: defaultSort.direction,
        isCustom: defaultSort.isCustom || false,
        scoreFn: defaultSort.scoreFn || null,
      });
      hasDefaultSorted.current = true;
    }
  }, [validData, defaultSort, loading]);

  // Separate effect to handle sortConfig changes (from user clicks or default sort)
  useEffect(() => {
    // Always apply filters when sortConfig has a key, regardless of whether it "changed"
    // This ensures default sort works on initial load
    if (sortConfig.key) {
      applyFilters();
    }
  }, [sortConfig]);

  const clearFilter = () => {
    setSearchTerm(""); // Reset text search
    setSearchColumns([]); // Clear column selection
    setInternalSearchQuery(null); // Reset structured query
    setFilteredData(validData); // Restore original data
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleSort = (key, isCustom = false, scoreFn = null) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }

    // Just update the sortConfig - the sortConfig useEffect will handle the actual sorting
    setSortConfig({
      key,
      direction,
      isCustom: isCustom || false,
      scoreFn: scoreFn || null,
    });
  };

  const toggleColumnVisibility = (column) => {
    setHiddenColumns((prevHidden) => {
      const newHiddenColumns = prevHidden.includes(column)
        ? prevHidden.filter((col) => col !== column) // Show column
        : [...prevHidden, column]; // Hide column

      return newHiddenColumns;
    });
  };

  const isDarkMode = theme.palette.mode === "dark";

  const visibleData = useMemo(
    () => filteredData.slice(0, visibleCount),
    [filteredData, visibleCount]
  );

  return (
    <WidgetContainer
      title={title + " - " + filteredData.length + " records"}
      description={discription}
      updatedAt={lastUpdatedDate}
      loaded={!loading}
      maximize={true}
      editMode={false}
      download={allowDownload}
      customWidgetActions={customWidgetActions}
      onDownload={() => {
        DownloadExcel(title, filteredData);
      }}
    >
      {/* Search Section */}
      {showSearch && Array.isArray(data) && data.length > 0 && (
        <Box
          sx={{
            backgroundColor: theme.palette.background.searchBar, // "#505050",
            padding: "0px",
            paddingLeft: "10px",
            paddingRight: "10px",
            borderRadius: "8px",
            marginTop: "0px",
            zIndex: 1,
            display: "flex",
            height: "60px",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" sx={{ height: "30px", flex: 1, gap: "10px" }}>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                multiple
                value={searchColumns}
                onChange={(e) => setSearchColumns(e.target.value)}
                displayEmpty
                renderValue={(selected) => (selected.length ? selected.join(", ") : "All Columns")}
                sx={{
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.searchFormControl,
                  height: "35px",
                  maxWidth: "200px",
                  borderRadius: "5px",
                }}
              >
                <MenuItem value="">
                  <em>All Columns</em>
                </MenuItem>
                {allColumns.map((column) => (
                  <MenuItem key={column} value={column}>
                    <Checkbox checked={searchColumns.includes(column)} />
                    <ListItemText primary={columnNameMapping[column] || column} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={searchOperator}
                onChange={(e) => setSearchOperator(e.target.value)}
                sx={{
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.searchFormControl,
                  height: "35px",
                  maxWidth: "200px",
                  borderRadius: "5px",
                }}
              >
                <MenuItem value="contains">Contains</MenuItem>
                <MenuItem value="equals">Equals</MenuItem>
                <MenuItem value="starts_with">Starts With</MenuItem>
                <MenuItem value="ends_with">Ends With</MenuItem>
              </Select>
            </FormControl>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{
                flex: 1,
                display: "flex",
                flexGrow: 1,
                backgroundColor: theme.palette.background.searchFormControl,
                height: "35px",
                borderRadius: "5px",
                "& .MuiInputBase-input": {
                  height: "18px",
                  color: theme.palette.text.primary,
                },
              }}
            />

            {isFiltering && <CircularProgress size={24} sx={{ color: "#FF9900" }} />}

            <IconButton title="Clear Filter" onClick={clearFilter}>
              <CancelPresentationIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>

            <IconButton title="Manage Columns" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <SplitscreenIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{
              disableListWrap: true,
              autoFocus: false,
            }}
            sx={{ maxHeight: 800, overflow: "hidden" }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.searchBar,
                zIndex: 10,
                padding: "8px",
              }}
            >
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search columns..."
                value={columnFilter}
                onChange={(e) => setColumnFilter(e.target.value.toLowerCase())}
                autoFocus
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  width: "100%",
                  backgroundColor: theme.palette.background.searchBarAccent,
                  color: "#fff",
                  borderRadius: "4px",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#808080",
                    },
                    "&:hover fieldset": {
                      borderColor: "#a0a0a0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FF9900",
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "#fff",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                width: "500px",
                overflowX: "hidden",
              }}
            >
              {allColumns
                .filter((col) => col.toLowerCase().includes(columnFilter))
                .sort((a, b) => a.localeCompare(b))
                .map((column) => (
                  <MenuItem
                    key={column}
                    sx={{ maxWidth: "500px" }}
                    onClick={() => toggleColumnVisibility(column)}
                  >
                    {hiddenColumns.includes(column) ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                    {columnNameMapping[column] || column}
                  </MenuItem>
                ))}
            </Box>
          </Menu>
        </Box>
      )}
      <Box
        sx={{
          overflow: "hidden",
          height: "100%",
          border: "1px solid transparent",
          borderRadius: "8px",
          padding: "0px",
          marginTop: showSearch ? "10px" : "0px",
        }}
      >
        <Box
          ref={tableContainerRef}
          sx={{
            overflow: "auto",
            height: "100%",

            marginBottom: "10px",
          }}
        >
          {!Array.isArray(data) || data.length < 1 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                fontSize: "16px",
                fontWeight: "bold",
                padding: "20px",
              }}
            >
              No data available
            </Box>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: theme.palette.background.searchBar,
                  zIndex: 10,
                }}
              >
                <tr
                  style={{
                    backgroundColor: theme.palette.background.searchBar,
                    color: theme.palette.text.primary,
                  }}
                >
                  {visibleColumns.map((column, index) => (
                    <th
                      title={columnNameMapping[column] || column.toUpperCase()}
                      key={column}
                      style={{
                        ...(index === 0
                          ? { ...styles.th, ...styles.firstTh }
                          : index === visibleColumns.length - 1
                            ? { ...styles.th, ...styles.lastTh }
                            : styles.th),
                        ...(dollarColumns[column] ? { textAlign: "center" } : {}),
                        // Conditional text alignment
                      }}
                      onClick={() => handleSort(column)}
                    >
                      {columnNameMapping[column] || column.toUpperCase()}
                      {sortConfig.key === column ? (
                        <span style={{ paddingLeft: "5px" }}>
                          {sortConfig.direction === "ascending" ? "▲" : "▼"}
                        </span>
                      ) : (
                        <span style={{ paddingLeft: "5px" }}>⇅</span>
                      )}
                    </th>
                  ))}
                  {customColumns &&
                    customColumns.map((column, i) => {
                      const isSorted = sortConfig.key === column.name;
                      const sortIcon = isSorted
                        ? sortConfig.direction === "ascending"
                          ? "▲"
                          : "▼"
                        : "⇅";

                      return (
                        <th
                          key={`custom-th-${column.name}-${i}`}
                          style={{
                            ...styles.actionsTh,
                            textAlign: column.align,
                            cursor: column.score ? "pointer" : "default",
                            color: column.score ? "#fff" : "#aaa",
                            ...(maxColumnWidths[column.name] && {
                              maxWidth: maxColumnWidths[column.name],
                              width: maxColumnWidths[column.name],
                            }),
                          }}
                          onClick={() =>
                            column.score && handleSort(column.name, true, column.score)
                          }
                          title={column.score ? `Click to sort by ${column.name}` : column.name}
                        >
                          {column.name} <span style={{ fontSize: "10px" }}>{sortIcon}</span>
                        </th>
                      );
                    })}
                  {showActionColumn && <th style={styles.actionsTh}>Actions</th>}
                </tr>
                {showTotalRow && (
                  <tr style={{ top: "-1px", position: "relative" }}>
                    {visibleColumns.map((column) => {
                      const isCurrency = !!dollarColumns[column]; // normalize to boolean
                      const shouldRender = allCurrency
                        ? dollarColumns[column] !== false
                        : isCurrency === true;

                      return shouldRender ? (
                        <td
                          key={column}
                          style={{
                            ...styles.td,
                            ...{
                              textAlign: "center",
                              fontWeight: "bold",
                              borderBottom: "2px solid #fff",
                            }, // Align center for dollar columns
                          }}
                        >
                          {`$${Number(
                            filteredData.reduce((acc, row) => acc + Number(row[column] || 0), 0)
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}`}
                        </td>
                      ) : (
                        <td
                          style={{
                            ...styles.td,
                            ...{
                              fontWeight: "bold",
                              borderBottom: "2px solid #fff",
                            },
                          }}
                          key={column}
                        >
                          {totalColumn == column && "Total"}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </thead>
              <tbody>
                {visibleData.map((row, index) => (
                  <tr
                    key={index}
                    style={{
                      backgroundColor: alternateRowColor
                        ? index % 2 === 0
                          ? isDarkMode
                            ? "#404040"
                            : "#f9f9f9"
                          : isDarkMode
                            ? "#505050"
                            : "#ffffff"
                        : isDarkMode
                          ? "#404040"
                          : "#ffffff",
                    }}
                  >
                    {visibleColumns.map((column) => (
                      <td
                        key={column}
                        style={{
                          ...styles.td,
                          ...(allCurrency
                            ? dollarColumns[column] !== false
                              ? { textAlign: "center" }
                              : {}
                            : dollarColumns[column] === true
                              ? { textAlign: "center" }
                              : {}),
                          ...(maxColumnWidths[column] && {
                            maxWidth: maxColumnWidths[column],
                          }),
                        }}
                      >
                        {(
                          allCurrency
                            ? dollarColumns[column] !== false
                            : dollarColumns[column] === true
                        )
                          ? `$${Number(row[column]).toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}`
                          : typeof row[column] === "boolean"
                            ? row[column]
                              ? "Yes"
                              : "No"
                            : row[column]}
                      </td>
                    ))}
                    {customColumns &&
                      customColumns.map((column, i) => (
                        <td
                          key={`custom-td-${column.name}-${i}`}
                          style={{
                            ...styles.td,
                            textAlign: column.align || "left",
                            ...(maxColumnWidths[column.name] && {
                              maxWidth: maxColumnWidths[column.name],
                              width: maxColumnWidths[column.name],
                            }),
                          }}
                        >
                          {column.render(row, i, filteredData, column)}
                        </td>
                      ))}
                    {showActionColumn && (
                      <td style={styles.actionsTd}>
                        <Tooltip title="View Details">
                          <IconButton
                            onClick={() => onActionClick(row)}
                            sx={{ color: theme.palette.text.primary }}
                          >
                            <PlagiarismIcon />
                          </IconButton>
                        </Tooltip>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Box>
      </Box>
    </WidgetContainer>
  );
};

export default React.memo(GenericTable);
